<script setup lang="ts">
import { formatting, Models, useLocalization, useOrderViewContext } from "@hantera/portal-app"
import { computed, ref } from "vue"
import Decimal from "decimal.js"
import StaticOrderLineDiscountEditor from "./StaticOrderLineDiscountEditor.vue";


const props = defineProps<{
  orderLine: Models.Graph.OrderLine
}>()

const t = useLocalization('components.order.orderLine.details')
const orderViewContext = useOrderViewContext()

const editing = ref<string>()

const listingValue = computed(() => {
  return props.orderLine.unitPrice.mul(props.orderLine.quantity)
})

const taxFactor = computed(() => {
  if (props.orderLine.taxFactor != null) {
    return props.orderLine.taxFactor
  }

  const net = orderViewContext.state.taxIncluded
    ? props.orderLine.orderLineTotal.sub(props.orderLine.taxTotal)
    : props.orderLine.orderLineTotal

  return net.isZero() ? new Decimal(0) : props.orderLine.taxTotal.div(net)
})


const discounts = computed(() => {

  return props.orderLine.calculatedDiscounts
    .map(cd => {

      if (cd.referenceType === 'staticDiscount') {
        const lineDiscount = props.orderLine.orderLineDiscounts.find(r => r.discountId === cd.referenceId)

        if (lineDiscount) {
          return {
            editable: true,
            referenceId: cd.referenceId,
            description: lineDiscount.description,
            calculatedValue: cd.value,
            percentage: cd.value.div(listingValue.value)
          }
        }

        const orderDiscount = orderViewContext.state.orderDiscounts.find(r => r.discountId === cd.referenceId)

        if (!orderDiscount) {
          return
        }

        return {
          referenceId: cd.referenceId,
          description: orderDiscount.description,
          calculatedValue: cd.value.mul(-1),
          percentage: cd.value.div(listingValue.value).mul(-1),
          badge: {
            label: 'Order',
            title: 'This discount is part of an order discount'
          }
        }
      } else if (cd.referenceType === 'promotion') {
        const promotion = orderViewContext.state.promotions.find(r => r.promotionId === cd.referenceId)
        if (!promotion) {
          return
        }
        return {
          referenceId: cd.referenceId,
          description: promotion.description,
          calculatedValue: cd.value.mul(-1),
          percentage: cd.value.div(listingValue.value).mul(-1)
        }
      }
    })
    .filter(d => d !== undefined)
})

function toggleEdit(referenceId: string) {
  if (editing.value === referenceId) {
    editing.value = undefined
  } else {
    editing.value = referenceId
  }
}

</script>

<template>
  
  <div class="discount-breakdown box p">

    <template
      v-for="discount in discounts" :key="discount.referenceId"
    >
      <div class="discount">
        <template v-if="editing !== discount.referenceId">
          <div class="label">
            <button class="btn-editable" @click="() => toggleEdit(discount.referenceId)" v-if="discount.editable">{{ discount.description }}</button>
            <span v-else>{{ discount.description }}</span> <Badge v-if="discount.badge" :title="discount.badge.title">{{ discount.badge.label }}</Badge>
          </div>
          <div class="percentage">
            {{ formatting.percent(discount.percentage) }}
          </div>
          <div class="value">
            {{ formatting.currency(discount.calculatedValue, orderViewContext.state.currencyCode) }}
          </div>
        </template>

        <StaticOrderLineDiscountEditor
          v-if="editing === discount.referenceId"
          :order-line="orderLine"
          :reference-id="discount.referenceId"
          class="edit-form"
          @close="() => toggleEdit(discount.referenceId)"
        />
      </div>
    </template>
    <div class="new-discount">
      <button class="btn-link" v-if="editing !== 'new'" @click="() => toggleEdit('new')">Add discount</button>
      <StaticOrderLineDiscountEditor
        v-if="editing === 'new'"
        :order-line="orderLine"
        class="edit-form"
        @close="() => toggleEdit('new')"
      />
    </div>

    <div class="tax-row">
      <div class="label">{{ t('tax') }}</div>
      <div class="percentage">
        {{ formatting.percent(taxFactor) }}
      </div>

      <div class="value">
        {{ formatting.currency(orderLine.taxTotal, orderViewContext.state.currencyCode) }}
      </div>
    </div>

  </div>
  
</template>

<style scoped>
.discount-breakdown {
  display: grid;
  grid-template-columns: auto min-content min-content;
  gap: 5px;
}

.discount {
  display: grid;
  grid-column: 1 / 4;
  grid-template-columns: subgrid;
}

.percentage,
.value {
  text-align: right;
}

.edit-form,
.new-discount {
  grid-column: 1 / 4;
}

.tax-row {
  display: grid;
  grid-column: 1 / 4;
  grid-template-columns: subgrid;
}
</style>
